import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, type FC } from "react";
import { type UrlObject } from "url";
import { usePage } from "./usePagination";

type PaginationProps = {
  totalResults: number;
  itemsPerPage?: number;
  preString?: string;
  nextString?: string;
};

const pageClassName =
  "font-bold mx-1.5 text-sm md:text-base hover:underline hover:opacity-80";
const pageNumerFocusClassName =
  "text-orange font-normal hover:no-underline hover:opacity-100";

const Pagination: FC<PaginationProps> = ({
  totalResults = 1,
  itemsPerPage = 24,
  preString: stringPre = "Più recenti",
  nextString: stringNext = "Meno recenti",
}) => {
  const page = usePage();
  const router = useRouter();
  const prevPage = page - 1;
  const prevPrevPage = page - 2;
  const nextNextPage = page + 2;

  const totalPage = Math.ceil(totalResults / itemsPerPage);

  const nextPages =
    totalPage - page + 1 > 0
      ? Array(Math.min(totalPage - page + 1, 3))
          .fill(0)
          .map((_, i) => i + page)
      : [];

  const generateHref = useCallback(
    (pageNumber: number): UrlObject | string => {
      if (pageNumber <= 0) return "";

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { page, ...otherQuery } = router.query;

      return {
        pathname: router.pathname,
        query: {
          ...otherQuery,
          page: pageNumber.toString(),
        },
      };
    },
    [router.query, router.pathname]
  );

  return (
    <div className="flex items-center justify-center pb-10 pt-14">
      <Link
        href={generateHref(page - 1)}
        aria-label="Pagina precedente"
        className={classNames(pageClassName, {
          ["hidden"]: page === 1 || totalResults <= itemsPerPage,
        })}
      >
        {`← ${stringPre}`}
      </Link>
      <Link
        href={generateHref(1)}
        aria-label="Pagina precedente"
        className={classNames(pageClassName, {
          [pageNumerFocusClassName]: 1 === page,
          ["hidden"]: totalResults <= itemsPerPage,
        })}
      >
        {1}
      </Link>
      {prevPrevPage > 0 &&
        page >= 5 &&
        prevPrevPage != 1 &&
        prevPrevPage != totalPage && <span>...</span>}
      {prevPrevPage > 0 && prevPrevPage != 1 && prevPrevPage != totalPage && (
        <Link
          href={generateHref(prevPrevPage)}
          aria-label={`Pagina ${prevPrevPage}`}
          className={pageClassName}
        >
          {prevPrevPage}
        </Link>
      )}
      {prevPage > 0 && prevPage != 1 && prevPage != totalPage && (
        <Link
          href={generateHref(prevPage)}
          aria-label={`Pagina ${prevPage}`}
          className={pageClassName}
        >
          {prevPage}
        </Link>
      )}

      {nextPages
        .filter((nextPage) => nextPage != 1 && nextPage != totalPage)
        .map((nextPage) => (
          <Link
            key={nextPage}
            href={generateHref(nextPage)}
            aria-label={`Pagina ${prevPrevPage}`}
            className={classNames(pageClassName, {
              [pageNumerFocusClassName]: nextPage === page,
            })}
          >
            {nextPage}
          </Link>
        ))}
      {page < totalPage - 3 &&
        nextNextPage != 1 &&
        nextNextPage != totalPage && <span>...</span>}
      <Link
        href={generateHref(totalPage)}
        aria-label="Pagina precedente"
        className={classNames(pageClassName, {
          [pageNumerFocusClassName]: totalPage === page,
        })}
      >
        {totalPage}
      </Link>
      <Link
        href={generateHref(totalPage === page ? page : page + 1)}
        aria-label="Pagina successiva"
        className={classNames(pageClassName, {
          ["hidden"]: totalPage === page,
        })}
      >
        {`${stringNext} →`}
      </Link>
    </div>
  );
};

export default Pagination;
